/* eslint-disable no-nested-ternary */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import { config } from '@brands/config/config';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { isStartableCaseType } from '@brands/services/cases/retiredCaseTypes';
import { notifySelectedCase } from '@brands/services/cases/selectedCase';
import { CaseType } from '@brands/services/cases/types/ICase';
import { triggerMobileHandoff } from '@brands/services/communication/triggerMobileHandover';
import { LOCATION_HANDOFF_TIMEOUT_MS, syncPatientLocation } from '@brands/hooks/usePostAuth';
import { getClientBranding } from '@brands/services/identity/getClientBranding';
import { getCurrentOrganization } from '@brands/services/identity/getCurrentOrganization';
import { getCurrentProfile } from '@brands/services/identity/getCurrentProfile';
import { getPricings } from '@brands/services/identity/getPricings';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectCurrentOrganization } from '@brands/store/selectors/organization';
import { setUser } from '@brands/store/slices/authSlice';
import { setClientBranding } from '@brands/store/slices/clientBrandingSlice';
import { setPatientState } from '@brands/store/slices/currentPatientState';
import { setIsFromHandoff } from '@brands/store/slices/isFromHandOffSlice';
import { setOrganizationPrices } from '@brands/store/slices/organizationPricingSlice';
import { setOrganization } from '@brands/store/slices/organizationSlice';
import { LinkedCaseState, setLinkedCase } from '@brands/store/slices/linkedCaseSlice';
import { setDependentId, setIsEditInsurance, setIsFromSelectPatient } from '@brands/store/slices/pageStateSlice';
import { FormValues, setExternalForms, setForm, setSelectedPatient } from '@brands/store/slices/patientFormSlice';
import { setSelectedPatientByAdmin } from '@brands/store/slices/selectedPatientSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { setSideNavColorByRole } from '@brands/Utils/getPrimaryColor';
import { cookieOptions } from '@brands/Utils/getToken';
import Cookies from 'js-cookie';
import queryString from 'query-string';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

// Define expected structure for decompressedState to improve type safety
interface DecompressedState {
  refreshToken: string;
  jwtTokenExpire: string;
  patientForm: {
    values: any;
    checkExternalForms: any;
    selectedPatient: any;
  };
  selectedPatient: any;
  pageState: {
    isFromSelectPatient: boolean;
    selectedDependentId: string | null;
    isEditInsuranceFlag: boolean;
  };
  patientState: Record<string, string>;
  organization_id?: string;
  // Which upcoming linked case (if any) the desktop tab was completing —
  // without this, a QR handoff mid-way through completing a linked case
  // loses the case id, its needs-payment ground truth, and the prior
  // clinical-data carried forward for it (see completeLinkedCase.ts).
  linkedCase?: LinkedCaseState;
}

interface IntakeIntent {
  caseType: CaseType;
}

const PrivateRoutesFC: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => queryString.parse(location.search), [location.search]);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  const { userInfo: userInfoInContext } = useAppSelector(selectAuth);
  const storedOrganization = useAppSelector(selectCurrentOrganization);
  const isAuthenticated = useMemo<boolean>(() => !!userInfoInContext, [userInfoInContext]);
  const dispatch = useAppDispatch();

  const handleUrlState = async (): Promise<void> => {
    try {
      // The desktop side renders the QR before the setUserWebState POST
      // returns, so a fast scanner can arrive here before the row exists.
      // Retry briefly with backoff — 500ms, 1s, 1.5s, 2s — covering ~5s of
      // race. The user is on a loading screen on the phone anyway.
      let userDetails = await getCurrentProfile();
      let decompressedState = userDetails.web_state?.state as DecompressedState | undefined;
      for (let attempt = 0; attempt < 4 && !decompressedState; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
        userDetails = await getCurrentProfile();
        decompressedState = userDetails.web_state?.state as DecompressedState | undefined;
      }

      if (!decompressedState) {
        throw new Error('No web state found');
      }

      Cookies.set('refreshToken', decompressedState.refreshToken, cookieOptions());
      Cookies.set('jwtTokenExpire', decompressedState.jwtTokenExpire, cookieOptions());

      // userInfo is intentionally not restored from the handoff payload:
      // initialize() runs immediately after this and calls getCurrentProfile()
      // → dispatch(setUser(...)) using the JWT cookie that handleUrlAuth has
      // already set. <Loading fullScreen /> covers the gap, so users never
      // see a blank state.
      dispatch(setForm(decompressedState.patientForm.values));
      dispatch(setExternalForms(decompressedState.patientForm.checkExternalForms));
      dispatch(setSelectedPatient(decompressedState.patientForm.selectedPatient));
      dispatch(setSelectedPatientByAdmin({ selectedPatient: decompressedState.selectedPatient }));
      dispatch(setIsFromSelectPatient(decompressedState.pageState.isFromSelectPatient));
      dispatch(setDependentId(decompressedState.pageState.selectedDependentId));
      dispatch(setIsEditInsurance(decompressedState.pageState.isEditInsuranceFlag));

      if (decompressedState.linkedCase?.linkedCaseId) {
        const { linkedCaseId, linkedCasePatientId, linkedCaseNeedsPayment, previousClinicalData } =
          decompressedState.linkedCase;
        dispatch(setLinkedCase({ linkedCaseId, linkedCasePatientId, linkedCaseNeedsPayment, previousClinicalData }));
      }

      const { patientState } = decompressedState;
      const [userId, state] = Object.entries(patientState)[0] || [];
      if (userId && state) {
        dispatch(setPatientState({ userId: Number(userId), state }));
      }

      // Re-fetch organization + pricings on this device instead of
      // restoring them from the payload — the desktop POST that lands
      // here is much shorter as a result, and these endpoints are cheap
      // GETs against the user's JWT-scoped context.
      //
      // Same race as the getCurrentProfile retry above: a phone landing
      // here right after a QR scan can hit this before its session is
      // fully warmed up. Left unretried, a single transient failure fell
      // through to the outer catch below, which only toasts and clears
      // `isInitializing` — the collector kept capturing images with
      // `currentOrganization` stuck at its null initial state, and case
      // submission silently fell back to the build's placeholder org
      // instead.
      const fetchOrganizationAndPricingWithRetry = async () => {
        let lastError: unknown;
        for (let attempt = 0; attempt < 5; attempt += 1) {
          try {
            // eslint-disable-next-line no-await-in-loop
            return await Promise.all([getCurrentOrganization(), getPricings()]);
          } catch (fetchError) {
            lastError = fetchError;
            if (attempt < 4) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
            }
          }
        }
        throw lastError;
      };
      const [currentOrganization, pricings] = await fetchOrganizationAndPricingWithRetry();
      dispatch(setOrganization({ currentOrganization }));
      dispatch(setOrganizationPrices({ currentOrganizationPrices: pricings }));

      await triggerMobileHandoff();
      // ✅ Clear large web state after it's been used
      navigate(location.pathname, { replace: true, state: {} });
    } catch (error: unknown) {
      displayErrorDetails(error);
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === 'persist:root' && userInfoInContext) {
        try {
          const newState = event.newValue ? JSON.parse(event.newValue) : null;
          const newAuth = newState?.auth ? JSON.parse(newState.auth) : null;
          const newUserId = newAuth?.userInfo?.id ?? null;
          if (newUserId !== userInfoInContext.id) {
            window.location.reload();
          }
        } catch {
          window.location.reload();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [userInfoInContext]);

  useEffect(() => {
    // `handleUrlState` retries `getCurrentProfile` for up to ~5s waiting
    // for the desktop POST to land. If the user navigates away during
    // that window the in-flight requests still resolve — without this
    // guard, the `.then(initialize)` chain dispatches setUser /
    // setOrganization and triggers a navigate() against the unmounted
    // tree, and a forward navigation starts a concurrent second run.
    let mounted = true;
    if (queryParams.auth) {
      dispatch(setIsFromHandoff(true));
      handleUrlAuth(queryParams.auth as string);
      void handleUrlState().then(() => {
        if (mounted) void initialize(); // run initialize only AFTER state is ready
      });
    } else {
      void initialize();
    }
    return () => {
      mounted = false;
    };
  }, [queryParams]);

  const handleUrlAuth = (url: string): void => {
    if (url) {
      Cookies.set('jwtToken', url, cookieOptions());
    }
  };

  const getPricingsFn = async (): Promise<void> => {};

  const getStartCaseParam = (): CaseType | null => {
    const raw = queryParams.start_case;
    // Membership in the CaseType enum is not enough: retired types are still
    // in it, and honoring one drops the patient into an intake for a service
    // that can no longer be delivered.
    return isStartableCaseType(raw) ? raw : null;
  };

  const getIntakeIntent = (): IntakeIntent | null => {
    const intentRaw = sessionStorage.getItem('intakeIntent');
    if (intentRaw) {
      try {
        const intent = JSON.parse(intentRaw) as Partial<IntakeIntent>;
        if (isStartableCaseType(intent.caseType)) {
          return { caseType: intent.caseType };
        }
      } catch {
        /* Ignore malformed stored intent and fall back to the URL param. */
      }
    }

    const caseType = getStartCaseParam();
    return caseType ? { caseType } : null;
  };

  const initialize = async (): Promise<void> => {
    try {
      const currentUserInfo = await getCurrentProfile();
      dispatch(setUser({ userInfo: currentUserInfo }));

      const clientCode = currentUserInfo.clients?.[0]?.code;
      if (clientCode) {
        try {
          const clientBranding = await getClientBranding(clientCode);
          dispatch(setClientBranding({ clientBranding }));
        } catch (error: unknown) {
          displayErrorDetails(error);
        }
      } else {
        // Overrides any leftover branding from a pre-auth /{clientCode} visit —
        // the account's own client tags are authoritative once logged in.
        dispatch(setClientBranding({ clientBranding: null }));
      }

      const roleName = currentUserInfo.role.name;
      const intent = getIntakeIntent();
      if (intent && roleName === UserRoleName.Patient) {
        try {
          const currentOrganization = await getCurrentOrganization();
          const pricings = await getPricings();

          dispatch(setOrganization({ currentOrganization }));
          dispatch(setOrganizationPrices({ currentOrganizationPrices: pricings }));
        } catch (error: unknown) {
          displayErrorDetails(error);
        }
        sessionStorage.removeItem('intakeIntent');

        // The ?start_case= shortcut skips the Dashboard entirely, which is also
        // where browser-geolocation location capture normally happens (Dashboard
        // mount effect / usePostAuth). Without this, SelectPatient would see an
        // unresolved location and read that as "service not available in your
        // location" instead of prompting the patient to select their state.
        // Best-effort and bounded: never block the redirect on a slow/denied
        // geolocation prompt — on failure syncPatientLocation itself opens
        // StateModal so SelectPatient can still resolve a location from the user.
        try {
          await Promise.race([
            syncPatientLocation(currentUserInfo, dispatch),
            new Promise<void>((resolve) => {
              setTimeout(resolve, LOCATION_HANDOFF_TIMEOUT_MS);
            }),
          ]);
        } catch {
          /* location capture is best effort; never block the redirect */
        }

        // Mirror dashboard case-type selection so CIO/GA still get
        // selected_case_type when the user arrives via ?start_case=.
        await notifySelectedCase(intent.caseType, undefined, 'start_case_param');

        const formPayload: FormValues[] = [
          {
            form_question_id: 20,
            value: {
              caseType: intent.caseType as CaseType,
            },
          },
        ];

        dispatch(setForm(formPayload));
        window.location.replace(`${window.location.origin}/${roleName}/select-patient`);

        return;
      }

      const currentPath = location.pathname;
      const currentSearch = location.search;
      const isAdminRole =
        currentUserInfo.role.name === UserRoleName.Admin ||
        currentUserInfo.role.name === UserRoleName.SuperAdmin ||
        currentUserInfo.role.name === UserRoleName.OrganizationAdmin;
      const isAdminReviewProfilePath = currentPath.startsWith(`/${currentUserInfo.role.name}/review-profile`);

      // Check if role is already part of the path (avoid infinite redirects).
      // Check all known role names case-insensitively so a URL like
      // /superadmin/... doesn't get /SuperAdmin prepended a second time.
      const pathLower = currentPath.toLowerCase();
      const pathAlreadyHasRole = Object.values(UserRoleName).some((role) => {
        const roleLower = role.toLowerCase();
        return pathLower.startsWith(`/${roleLower}/`) || pathLower === `/${roleLower}`;
      });

      if (!pathAlreadyHasRole && !(isAdminRole && isAdminReviewProfilePath)) {
        // Prepend role to path and force full reload. Like the other alias
        // redirects in this try block, this replaces the current entry instead of pushing:
        // the page being left only ever showed a spinner, and going Back to it
        // (or navigate(-1) from the next page) restores it from the
        // back/forward cache still frozen on that spinner.
        const newUrl = `${window.location.origin}/${roleName}${currentPath}${currentSearch}`;
        window.location.replace(newUrl);
        return; // stop further logic
      }

      // Existing role-based redirects
      if (location.pathname === '/dashboard') {
        window.location.replace(`${window.location.origin}/${roleName}/dashboard${currentSearch}`);
        return;
      }

      if (
        location.pathname.includes('/select-patient') &&
        !location.pathname.toLowerCase().startsWith(`/${roleName.toLowerCase()}/select-patient`)
      ) {
        const caseType = location.pathname.split('/').pop() || 'default';
        window.location.replace(`${window.location.origin}/${roleName}/select-patient/${caseType}${currentSearch}`);
        return;
      }

      if (isAdminRole && location.pathname === '/my-profile') {
        window.location.replace(`${window.location.origin}/${roleName}/my-profile${currentSearch}`);
        return;
      }

      // A patient's organization and pricing are loaded by the Dashboard, and
      // sign-in clears both. A link that sends a logged-out patient through
      // sign-in straight to a deeper page (the "Complete my intake" email
      // opens the intake wizard from My Care Journey) never visits the
      // Dashboard, so the wizard checked the chosen state against no
      // organization and reported every state as "service not available".
      // Load them before the page renders, as the ?start_case= path does.
      if (roleName === UserRoleName.Patient && !storedOrganization) {
        try {
          const [currentOrganization, pricings] = await Promise.all([getCurrentOrganization(), getPricings()]);
          dispatch(setOrganization({ currentOrganization }));
          dispatch(setOrganizationPrices({ currentOrganizationPrices: pricings }));
        } catch (error: unknown) {
          displayErrorDetails(error);
        }
      }

      setIsInitializing(false);
    } catch (error) {
      // Don't call signOut() or Cookies.remove() here. Cookies are scoped
      // to the domain, not the tab — wiping them logs the user out of
      // every other dentistry.one tab, including ones whose session is
      // perfectly fine (see DO-3955, where a cross-site redirect from
      // JotForm stripped cookies on this tab's request, landing init()
      // in the catch and taking the background dashboard tab down with
      // it). Clear redux so this tab renders the login screen; leave
      // cookies alone so healthy tabs keep working. The next successful
      // sign-in overwrites the cookie anyway.
      dispatch(setUser({ userInfo: null }));
      setIsInitializing(false);
      // Keep the hash too: it picks the tab on pages like My Care, which
      // email and notification links open directly.
      window.location.href = `/?redirect_uri=${encodeURIComponent(
        location.pathname + location.search + location.hash,
      )}`;
    }
  };

  if (isInitializing) {
    return <Loading fullScreen />;
  }

  return isAuthenticated ? <Outlet /> : <Navigate to="/" replace />;
};

export default PrivateRoutesFC;
